import styles from './SupportingItemChecklist.module.css'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { capturedInformationCardId } from '@/features/jobs/details/components/CapturedInformationCard'

export interface SupportingItemChecklistItem {
  name: string
  required: boolean
  completed: boolean
  adHoc: boolean
  providedText: string
  /**
   * Whether pressing the item scrolls the captured information card into
   * view.
   */
  revealsCapturedInformation?: boolean
  /**
   * Set only when the item links to where its information is shown.
   */
  onPress?: () => void
}

export interface SupportingItemChecklistSection {
  name: string
  heading: string
  completedCount: number
  totalCount: number
  items: SupportingItemChecklistItem[]
}

export interface SupportingItemChecklistProps {
  sections: SupportingItemChecklistSection[]
}

export function SupportingItemChecklist({
  sections,
}: SupportingItemChecklistProps) {
  return (
    <div>
      {sections.map((section, key) => {
        return <SupportingItemSection key={key} section={section} />
      })}
    </div>
  )
}

interface SupportingItemSectionProps {
  section: SupportingItemChecklistSection
}

function SupportingItemSection({ section }: SupportingItemSectionProps) {
  return (
    <div data-test={`checklist-supporting-items-section-${section.name}`}>
      <div className={styles.sectionHeader}>
        {section.heading}
        <div className={styles.sectionHeaderSubtext}>
          ({`${section.completedCount}/${section.totalCount}`})
        </div>
      </div>
      <div>
        {section.items.map((item, index) => {
          const isPressable = item.onPress !== undefined
          const handlePress = item.onPress && pressHandler(item)
          const testId = `checklist-supporting-items-item-${item.name}`
          const content = (
            <>
              <div
                className={cx(styles.icon, getItemStyle(item))}
                data-test={`checklist-supporting-items-item-${item.name}-completion-status`}
              >
                {item.completed ? (
                  <Icon name="tick" />
                ) : item.adHoc ? (
                  <Icon name="alert-avatar" />
                ) : (
                  <Icon name="circle" />
                )}
              </div>
              <div
                data-test={`checklist-supporting-items-item-${item.name}-name`}
                className={styles.name}
              >
                {item.name}
                {item.required && (
                  <span
                    data-test={`checklist-supporting-items-item-${item.name}-required`}
                    className={styles.required}
                  >
                    *
                  </span>
                )}
              </div>
              <div
                className={cx(styles.providedInfo, getItemStyle(item))}
                data-test={`checklist-supporting-items-item-${item.name}-provided`}
              >
                {item.providedText}
              </div>
              {isPressable && (
                <div className={styles.linkIcon}>
                  <Icon name="arrow" size={16} />
                </div>
              )}
            </>
          )
          return handlePress ? (
            <RawButton
              key={index}
              testId={testId}
              className={cx(styles.supportingItem, styles.clickable)}
              onPress={handlePress}
            >
              {content}
            </RawButton>
          ) : (
            <div
              key={index}
              data-test={testId}
              className={styles.supportingItem}
            >
              {content}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function pressHandler(item: SupportingItemChecklistItem) {
  return () => {
    item.onPress?.()
    if (item.revealsCapturedInformation) {
      document
        .getElementById(capturedInformationCardId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }
}

function getItemStyle(item: SupportingItemChecklistItem) {
  return item.completed
    ? styles.provided
    : item.adHoc
      ? styles.requested
      : styles.notProvided
}
